/**
 * Pure geometry and data helpers for GeoMap (no MapLibre import, so they are unit-testable).
 * Coordinates are [lng, lat] everywhere, as in GeoJSON.
 */
import type { Feature, FeatureCollection } from 'geojson';

export type LngLat = [number, number];

/** Colour roles a map feature can take; resolved from design tokens at render time. */
export type GeoTone = 'primary' | 'good' | 'medium' | 'bad' | 'neutral' | 'accent' | 'caution' | 'info';

export const TONE_TOKENS: Record<GeoTone, `--${string}`> = {
  primary: '--color-primary',
  good: '--color-success',
  medium: '--color-warning',
  bad: '--color-danger',
  neutral: '--color-neutral',
  accent: '--color-accent',
  caution: '--color-caution',
  info: '--color-info',
};

export interface GeoPoint {
  id: string;
  lng: number;
  lat: number;
  tone?: GeoTone;
  /** Data-driven colour (a place or group colour stored as data); wins over `tone`. */
  color?: string | null;
  label?: string;
  /** Degrees clockwise from north; draws a direction arrow when the layer has `arrows`. */
  heading?: number | null;
  selected?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Drawn faded (e.g. filtered out but kept for context). */
  muted?: boolean;
}

export interface GeoPointLayer {
  kind: 'points';
  id: string;
  /** Counted when fitting the view (default true); false for context layers. */
  fit?: boolean;
  points: GeoPoint[];
  /** Group nearby points into counted clusters (fast with hundreds of units). */
  cluster?: boolean;
  /** Labels under the points: always, or only when zoomed in (and for the selected point). */
  labels?: 'always' | 'zoomed' | false;
  arrows?: boolean;
  interactive?: boolean;
}

export interface GeoLine {
  id: string;
  coordinates: LngLat[];
  tone?: GeoTone;
  color?: string | null;
}

export interface GeoLineLayer {
  kind: 'lines';
  id: string;
  /** Counted when fitting the view (default true); false for context layers. */
  fit?: boolean;
  lines: GeoLine[];
  width?: number;
  dashed?: boolean;
  interactive?: boolean;
}

interface ShapeBase {
  id: string;
  label?: string;
  tone?: GeoTone;
  color?: string | null;
  selected?: boolean;
}

export type GeoShape = ShapeBase & ({ type: 'circle'; center: LngLat; radius_m: number } | { type: 'polygon'; points: LngLat[] });

export interface GeoShapeLayer {
  kind: 'shapes';
  id: string;
  /** Counted when fitting the view (default true); false for context layers. */
  fit?: boolean;
  shapes: GeoShape[];
  labels?: boolean;
  interactive?: boolean;
}

export type GeoLayer = GeoPointLayer | GeoLineLayer | GeoShapeLayer;

const EARTH_RADIUS_M = 6_371_008.8;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** A circle on the ground as a closed ring of `steps` points (spherical approximation). */
export function circleRing(center: LngLat, radiusM: number, steps = 64): LngLat[] {
  const [lng, lat] = center;
  const d = radiusM / EARTH_RADIUS_M;
  const la1 = rad(lat);
  const lo1 = rad(lng);
  const ring: LngLat[] = [];
  for (let i = 0; i < steps; i++) {
    const b = (2 * Math.PI * i) / steps;
    const la2 = Math.asin(Math.sin(la1) * Math.cos(d) + Math.cos(la1) * Math.sin(d) * Math.cos(b));
    const lo2 = lo1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(la1), Math.cos(d) - Math.sin(la1) * Math.sin(la2));
    ring.push([deg(lo2), deg(la2)]);
  }
  ring.push(ring[0]!);
  return ring;
}

/** Great-circle distance in metres. */
export function distanceM(a: LngLat, b: LngLat): number {
  const dLat = rad(b[1] - a[1]);
  const dLng = rad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** A polygon ring, closed. */
export function closedRing(points: LngLat[]): LngLat[] {
  if (points.length === 0) return [];
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return first[0] === last[0] && first[1] === last[1] ? points : [...points, first];
}

/** The ring a shape covers on the map. */
export function shapeRing(shape: GeoShape): LngLat[] {
  return shape.type === 'circle' ? circleRing(shape.center, shape.radius_m) : closedRing(shape.points);
}

/** Where to put a shape's label or pin: the centre of a circle, the vertex average of a polygon. */
export function shapeAnchor(shape: GeoShape): LngLat | null {
  if (shape.type === 'circle') return shape.center;
  const pts = shape.points;
  if (!pts.length) return null;
  const sum = pts.reduce<[number, number]>((acc, p) => [acc[0] + p[0], acc[1] + p[1]], [0, 0]);
  return [sum[0] / pts.length, sum[1] / pts.length];
}

export type Bounds = [LngLat, LngLat];

/** South-west and north-east corners around every coordinate given, or null when none. */
export function boundsOf(coords: Iterable<LngLat>): Bounds | null {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const [lng, lat] of coords) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    w = Math.min(w, lng);
    e = Math.max(e, lng);
    s = Math.min(s, lat);
    n = Math.max(n, lat);
  }
  return Number.isFinite(w)
    ? [
        [w, s],
        [e, n],
      ]
    : null;
}

/** Every coordinate of the layers (for fitting the view). */
export function* layerCoordinates(layers: GeoLayer[]): Generator<LngLat> {
  for (const layer of layers) {
    if (layer.fit === false) continue;
    if (layer.kind === 'points') for (const p of layer.points) yield [p.lng, p.lat];
    else if (layer.kind === 'lines') for (const l of layer.lines) yield* l.coordinates;
    else for (const sh of layer.shapes) yield* shapeRing(sh);
  }
}

/** Index of the coordinate nearest to `p` (planar, fine at city scale), or -1. */
export function nearestIndex(coords: readonly LngLat[], p: LngLat): number {
  let best = -1;
  let bestD = Infinity;
  const k = Math.cos(rad(p[1]));
  for (let i = 0; i < coords.length; i++) {
    const c = coords[i]!;
    const dx = (c[0] - p[0]) * k;
    const dy = c[1] - p[1];
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

type Resolve = (tone: GeoTone | undefined, color: string | null | undefined) => string;

/** GeoJSON for a points layer; colours are resolved here so the style reads them with ['get']. */
export function pointFeatures(layer: GeoPointLayer, resolve: Resolve): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: layer.points
      .filter((p) => Number.isFinite(p.lng) && Number.isFinite(p.lat))
      .map((p) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        properties: {
          fid: p.id,
          color: resolve(p.tone, p.color),
          label: p.label ?? '',
          heading: p.heading ?? null,
          hasHeading: p.heading != null,
          selected: !!p.selected,
          muted: !!p.muted,
          size: p.size ?? 'md',
        },
      })),
  };
}

export function lineFeatures(layer: GeoLineLayer, resolve: Resolve): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: layer.lines
      .filter((l) => l.coordinates.length > 1)
      .map((l) => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: l.coordinates },
        properties: { fid: l.id, color: resolve(l.tone, l.color) },
      })),
  };
}

/** Polygons for the fills and outlines, plus one point per shape for its label. */
export function shapeFeatures(layer: GeoShapeLayer, resolve: Resolve): FeatureCollection {
  const features: Feature[] = [];
  for (const sh of layer.shapes) {
    const ring = shapeRing(sh);
    if (ring.length < 4) continue;
    const props = { fid: sh.id, color: resolve(sh.tone, sh.color), label: sh.label ?? '', selected: !!sh.selected };
    features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: props });
    const anchor = shapeAnchor(sh);
    if (anchor) features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: anchor }, properties: props });
  }
  return { type: 'FeatureCollection', features };
}
